<style lang="postcss">
	.range-pips {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		height: 2.5em;
		align-items: flex-start;
		position: relative;
		padding: 0.375em 0.5em 0 0.5em;
	}
	.pip-line {
		position: absolute;
		cursor: pointer;
		top: 0;
		width: 1.25px;
		height: 0;
		background-color: rgba(212, 212, 212, 1);
		transform: translate3d(-50%, 0, 0);
		border-bottom-right-radius: 0.25rem;
		border-bottom-left-radius: 0.25rem;
	}
	.pip {
		font-weight: 445;
		position: absolute;
		white-space: nowrap;
		padding-top: 0.6em;
		font-size: 0.775rem;
		opacity: 0.4;
		transition: all 0.15s ease;
		transform: translate3d(-50%, 0, 0);
	}
	.pip.first {
		left: 0;
		transform: translate3d(0%, 0, 0);
	}
	.pip.last {
		right: 0;
		transform: translate3d(0%, 0, 0);
	}
	.pip.selected,
	.pip:hover {
		color: var(--theme-color);
		opacity: 1;
	}
</style>

<script lang="ts">
	import { onMount, tick } from "svelte";
	import { fade } from "$lib/transitions";
	import { BROWSER } from "esm-env";
	export let min: number = 0;
	export let max: number = 100;
	export let step: number = 10;
	export let internalValue: number;
	export let reversed = false;
	export let hoverable = true;
	export let disabled = false;
	export let leftLabel = "";
	export let rightLabel = "";
	export let display: (v: number) => string;
	export let focusDelay: boolean;
	export let value: ChoiceValue = undefined;
	export let type: "labels" | "numbers" = "labels";

	let parentDiv: HTMLElement;
	let pipCount: number = isNaN(Math.round((max - min) / step))
		? 0
		: Math.max(Math.round((max - min) / step), 0);

	// range pips / values props
	export let pipstep: number = 1;
	$: extraPips = [50].map((p) => getPercentile(pipCount, p));

	// stylistic props
	export let focus: boolean;
	export let orientationStart: "left" | "right" = "left";

	// methods
	export let percentOf: CallbackFn;
	export let moveHandle: (v: number) => void;
	export let fixFloat: (v: number) => number;

	$: stepFixed = step || 1;
	$: maxFixed = max || 100;
	$: minFixed = min || 0;
	$: pipStep =
		pipstep ||
		((maxFixed - minFixed) / stepFixed >= 100 ? (maxFixed - minFixed) / 20 : 1);
	$: pipCount = Math.max(
		0,
		Math.round((maxFixed - minFixed) / (stepFixed * pipStep))
	);

	function isSelected(val: number) {
		const selected =
			typeof value === "undefined"
				? false
				: fixFloat(internalValue) === fixFloat(val);
		return selected;
	}

	function labelClick(val: number) {
		if (!disabled) {
			moveHandle(val);
		}
	}

	function getPercentile(count, percentile) {
		if (percentile < 0 || percentile > 100) {
			throw new Error("Percentile must be between 0 and 100");
		}

		const result = Math.floor((percentile / 100) * count);
		return result;
	}
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<div
	bind:this="{parentDiv}"
	class="range-pips"
	class:disabled="{disabled}"
	class:hoverable="{hoverable}"
	class:reversed="{reversed}"
	class:focus="{focus}">
	<span
		class="pip first"
		role="button"
		aria-label="{leftLabel || display(min)}"
		tabindex="-1"
		class:hidden="{isSelected(min) ||
			(focusDelay === false && type === 'labels')}"
		on:click="{() => labelClick(min)}"
		on:touchend|preventDefault="{() => labelClick(min)}">
		{leftLabel || display(min)}
	</span>
	{#each Array(pipCount) as _, i}
		{#if i !== 0 && i * step !== max && extraPips.includes(i)}
			<span
				transition:fade="{{ duration: 100 }}"
				class="-translate-x-1/2 pip"
				role="button"
				aria-label="{display(min + i * step)}"
				tabindex="-1"
				class:selected="{isSelected(min + i * step)}"
				style="{orientationStart}: {percentOf(min + i * step)}%;"
				on:click="{() => labelClick(min + i * step)}"
				on:touchend|preventDefault="{() => labelClick(min + i * step)}">
				{display(min + i * step)}
			</span>
		{/if}
	{/each}

	<span
		class="pip last"
		role="button"
		aria-label="{rightLabel || display(max)}"
		tabindex="-1"
		class:hidden="{isSelected(max) ||
			(focusDelay === false && type === 'labels')}"
		on:click="{() => labelClick(max)}"
		on:touchend|preventDefault="{() => labelClick(max)}">
		{rightLabel || display(max)}
	</span>
</div>
